import { getTimezoneOffset } from "date-fns-tz";
import React from "react";
import { Select } from "antd";
import zones from "./zones.json";

type Props = {
  timeZone: string | null;
  onChange: (timeZone: string | null) => void;
};

type ZoneOption = {
  id: string;
  name: string;
  offset: number;
};

let cachedZoneOptions: ZoneOption[] | null = null;

const TimeZoneInput: React.FC<Props> = ({ timeZone, onChange }) => {
  const [zoneOptions, setZoneOptions] = React.useState(cachedZoneOptions);

  React.useEffect(() => {
    if (zoneOptions !== null) return;

    const date = new Date();
    cachedZoneOptions = zones
      .flatMap((zone) => {
        try {
          const offset = getTimezoneOffset(zone, date) / 3_600_000;
          const offsetFormatted = (offset >= 0 ? "+" : "-") + Math.abs(offset);
          return {
            id: zone,
            name: `(UTC${offsetFormatted}) ${zone.replaceAll("_", " ")}`,
            offset,
          };
        } catch {
          return [];
        }
      })
      .sort((a, b) => {
        const delta = a.offset - b.offset;
        return delta === 0 ? a.name.localeCompare(b.name) : delta;
      });
    setZoneOptions(cachedZoneOptions);
  }, []);

  const options = zoneOptions
    ? [{ value: "", label: "Automatic" }, ...zoneOptions.map((z) => ({ value: z.id, label: z.name }))]
    : [{ value: "", label: "Automatic" }, { value: "", label: "Loading...", disabled: true }];

  return (
    <Select
      showSearch
      size="small"
      style={{ width: "100%", minWidth: 0 }}
      value={timeZone || ""}
      onChange={(val) => onChange(val || null)}
      filterOption={(input, option) =>
        (option?.label as string || "").toLowerCase().includes(input.toLowerCase())
      }
      options={options}
      getPopupContainer={(trigger) => trigger.parentElement || document.body}
    />
  );
};

export default TimeZoneInput;
